import { useGlobalSearchParams } from "expo-router";
import { useEffect, useRef } from "react";
import { View as RNView } from "react-native";
import { YStack } from "tamagui";

import { trackServerEvent } from "@/api/mutations/analytics";
import {
  useIsChatMutationPending,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import { useAuthState } from "@/hooks/useAuthState";
import useRefsToTrackScrollProgress from "@/hooks/useRefsToTrackScrollProgress";
import { useWaitTimeBanner } from "@/hooks/useWaitTimeBanner";
import AnnouncementBanner from "./AnnouncementBanner";
import ClinicianCta from "./ClinicianCta";
import GeneralCareHero, { type WelcomeChip } from "./GeneralCareHero";
import HealthCoverage from "./HealthCoverage";
import HowItWorks from "./HowItWorks";
import LegalDisclaimer from "./LegalDisclaimer";
import MedicalGroupTrust from "./MedicalGroupTrust";
import PatientTestimonials from "./PatientTestimonials";
import ReadyWhenYouAre from "./ReadyWhenYouAre";
import WaitTimeBanner from "./WaitTimeBanner";

const SECTION_NAMES = [
  "hero",
  "how-it-works",
  "clinician-cta",
  "health-coverage",
  "medical-group-trust",
  "patient-testimonials",
  "ready-when-you-are",
  "legal-disclaimer",
];

export type PartnerLandingConfig = {
  welcomeChip?: WelcomeChip;
  announcement?: {
    message: string;
    linkHref?: string;
    externalLinkHref?: string;
  };
  visitedSplashEventName: string;
  showPromoDisclaimer?: boolean;
};

type GeneralCareLandingPageProps = {
  partnerConfig: PartnerLandingConfig;
};

export default function GeneralCareLandingPage({
  partnerConfig,
}: GeneralCareLandingPageProps) {
  const sectionRefs = useRefsToTrackScrollProgress(SECTION_NAMES, {});
  const { sendNoriMessage } = useSendNoriMessage();
  const isPending = useIsChatMutationPending();
  const { isInitializing } = useAuthState();
  const tracked = useRef(false);
  const params = useGlobalSearchParams();
  const { showWaitTimeBanner, waitTimeCopy } = useWaitTimeBanner();

  useEffect(() => {
    if (isInitializing || tracked.current) return;
    tracked.current = true;
    trackServerEvent(partnerConfig.visitedSplashEventName, {
      ...params,
      provider_id: "UC_CURAI",
    });
  }, [isInitializing, params, partnerConfig.visitedSplashEventName]);

  return (
    <YStack width="100%" alignItems="stretch">
      {showWaitTimeBanner ? (
        <WaitTimeBanner waitTimeCopy={waitTimeCopy} />
      ) : partnerConfig.announcement ? (
        <AnnouncementBanner
          message={partnerConfig.announcement.message}
          linkHref={partnerConfig.announcement.linkHref}
          externalLinkHref={partnerConfig.announcement.externalLinkHref}
        />
      ) : null}
      <RNView ref={sectionRefs["hero"]}>
        <GeneralCareHero welcomeChip={partnerConfig.welcomeChip} />
      </RNView>
      <RNView ref={sectionRefs["how-it-works"]}>
        <HowItWorks />
      </RNView>
      <RNView ref={sectionRefs["clinician-cta"]}>
        <ClinicianCta
          onPress={() => sendNoriMessage("Talk to a doctor")}
          disabled={isPending}
        />
      </RNView>
      <RNView ref={sectionRefs["health-coverage"]}>
        <HealthCoverage
          onPress={() => sendNoriMessage("Talk to a doctor")}
          disabled={isPending}
        />
      </RNView>
      <RNView ref={sectionRefs["medical-group-trust"]}>
        <MedicalGroupTrust />
      </RNView>
      <RNView ref={sectionRefs["patient-testimonials"]}>
        <PatientTestimonials />
      </RNView>
      <RNView ref={sectionRefs["ready-when-you-are"]}>
        <ReadyWhenYouAre />
      </RNView>
      <RNView ref={sectionRefs["legal-disclaimer"]}>
        <LegalDisclaimer
          showPromoDisclaimer={partnerConfig.showPromoDisclaimer}
        />
      </RNView>
    </YStack>
  );
}
